import '../../styles/web-components/global-banner.scss';
import { createState } from '../helpers/state-subject';
import { preventAnimation, accessibilityEnabledListener } from '../services/accessibility.service';
import { createFocusTrap } from '../services/focus-trap.service';
import { headerTransition, updateBodyPaddingTop, getElementOrParentByClassName } from '../helpers/utils';
import { isMobile } from '../constants/devices.constants';
const LEFT = -1;
const RIGHT = 1;
const CLICK_BUFFER = 3; // buffer in pixels to prevent a click from being interpreted as a swipe
const UPDATE_BANNERS_EVENT = "global-banner:updateBanners";
class GlobalBanner extends HTMLElement {
    device;
    container;
    banners;
    arrows;
    rotationIntervalId = null;
    selectedBannerIdStore;
    setupAc = new AbortController();
    swipeListenersAdded = false;
    focusTrap;
    headerTransitionUnsub;
    updateBannersFunc = () => {
        console.debug(UPDATE_BANNERS_EVENT);
        this.reset();
    };
    constructor() {
        super();
        this.addEventListener("click", (e) => {
            const target = e.target;
            const codeButtonEl = getElementOrParentByClassName(target, 'js-global-banner-code-button');
            if (codeButtonEl) {
                this.copyCode(codeButtonEl);
                return;
            }
            const detailsToggleEl = getElementOrParentByClassName(target, 'js-global-banner-detail-toggle');
            if (detailsToggleEl) {
                this.toggleDetails(detailsToggleEl.closest('.global-banner__banner'));
                return;
            }
            const detailsCloseEl = getElementOrParentByClassName(target, 'js-global-banner-detail-close');
            if (detailsCloseEl) {
                this.toggleDetails(detailsCloseEl.closest('.global-banner__banner'), true);
                return;
            }
        });
    }
    connectedCallback() {
        this.device = this.getAttribute("display");
        const config = window.UTK_themeConfig && window.UTK_themeConfig[`${this.device}GlobalBanners`];
        this.container = this.querySelector('.global-banner');
        this.selectedBannerIdStore = createState(undefined);
        window.addEventListener(UPDATE_BANNERS_EVENT, this.updateBannersFunc);
        this.headerTransitionUnsub = headerTransition.subscribe((smallerHeader) => {
            this.display(smallerHeader);
        });
        if (config?.banners?.length) {
            this.setup();
        }
    }
    disconnectedCallback() {
        window.removeEventListener(UPDATE_BANNERS_EVENT, this.updateBannersFunc);
        this.headerTransitionUnsub?.();
        this.headerTransitionUnsub = undefined;
        this.stopRotation();
        this.setupAc.abort();
        this.setupAc = new AbortController();
    }
    setup() {
        // tear down any prior setup-scoped listeners (handles reset cycles)
        this.setupAc.abort();
        this.setupAc = new AbortController();
        this.swipeListenersAdded = false;
        const { signal } = this.setupAc;
        this.banners = Array.from(this.container.querySelectorAll(".js-global-banner:not(.js-global-banner-dynamic)"));
        this.arrows = Array.from(this.container.querySelectorAll(".js-global-banner-arrow-button"));
        if (this.banners.length && !this.container.classList.contains('global-banner--has-initial-banners')) {
            // add initial banner class if dynamic banners added
            this.container.classList.add('global-banner--has-initial-banners');
        }
        if (this.banners.length > 1) {
            console.log(`GlobalBanner: setting up for multiple global banners for ${this.device}`);
            const unsubBanner = this.selectedBannerIdStore.subscribe((id) => {
                this.banners.forEach((b) => {
                    if (b.dataset.id === id) {
                        b.classList.add("global-banner__banner--selected");
                    }
                    else {
                        b.classList.remove("global-banner__banner--selected");
                    }
                    this.toggleDetails(b, true);
                });
            });
            signal.addEventListener('abort', () => unsubBanner());
            this.selectedBannerIdStore.set(this.banners[0].dataset.id);
            this.arrows.forEach((arrow) => {
                arrow.addEventListener('click', (e) => {
                    const el = e.currentTarget;
                    this.selectedBannerIdStore.set(this.getNextBannerId(el.dataset.direction === "prev" ? LEFT : RIGHT));
                    this.resetRotation();
                }, { signal });
            });
            this.startRotation();
            this.container.addEventListener('mouseenter', () => this.stopRotation(), { signal });
            this.container.addEventListener('mouseleave', () => this.startRotation(), { signal });
            const unsubA11y = accessibilityEnabledListener().subscribe(() => this.stopRotation());
            signal.addEventListener('abort', () => unsubA11y());
            if (this.device === "mobile" && !this.swipeListenersAdded) {
                this.addSwipeListeners(this.container, signal);
            }
            this.container.classList.remove("global-banner--hide-controls");
        }
        else if (this.banners.length === 1) {
            console.log(`GlobalBanner: setting up for one global banner for ${this.device}`);
            this.banners[0].classList.add("global-banner__banner--selected");
            // single banner: no rotation needed
        }
    }
    reset() {
        this.debug("refreshing setup");
        this.stopRotation();
        this.setup();
    }
    getNextBannerId(direction) {
        const selectedIdx = this.banners.findIndex((b) => b.classList.contains("global-banner__banner--selected"));
        const nextIdx = selectedIdx + (direction * 1);
        const newIdx = nextIdx === (this.banners.length) ? 0 : nextIdx === -1 ? this.banners.length - 1 : nextIdx;
        const nextBanner = this.banners[newIdx];
        return nextBanner.dataset.id;
    }
    stopRotation() {
        this.debug("stopping rotation");
        if (this.rotationIntervalId !== null) {
            clearInterval(this.rotationIntervalId);
            this.rotationIntervalId = null;
        }
    }
    startRotation() {
        const rotationSpeed = +this.getAttribute("rotation");
        if (!preventAnimation() && this.rotationIntervalId === null) {
            this.debug("starting rotation");
            if (rotationSpeed) {
                const rotationMillis = rotationSpeed * 1000;
                this.rotationIntervalId = setInterval(() => {
                    this.selectedBannerIdStore.set(this.getNextBannerId(RIGHT));
                }, rotationMillis);
            }
        }
    }
    resetRotation() {
        this.debug("resetting rotation");
        this.stopRotation();
        this.startRotation();
    }
    addSwipeListeners(el, signal) {
        this.debug("adding swipe listeners");
        let touchstartX = 0;
        let touchendX = 0;
        el.addEventListener('touchstart', e => {
            this.debug("touchstart");
            if (this.isBanner(e.target)) {
                touchstartX = e.changedTouches[0].screenX;
            }
        }, { passive: true, signal });
        el.addEventListener('touchend', e => {
            this.debug("touchend");
            if (this.isBanner(e.target)) {
                touchendX = e.changedTouches[0].screenX;
                const diff = Math.abs(touchstartX - touchendX);
                if (diff < CLICK_BUFFER) {
                    // click
                    return;
                }
                let direction = RIGHT;
                if (touchendX > touchstartX) {
                    direction = LEFT;
                }
                this.selectedBannerIdStore.set(this.getNextBannerId(direction));
                this.resetRotation();
            }
        }, { signal });
        this.swipeListenersAdded = true;
    }
    async copyCode(el) {
        const width = getComputedStyle(el).width;
        el.style.width = width; // keep width consistent regardless of content
        const children = Array.from(el.children);
        children.forEach(c => c.classList.toggle("hidden"));
        const code = el.dataset.code;
        try {
            await navigator.clipboard.writeText(code);
        }
        catch (e) {
            console.error(e);
        }
        setTimeout(() => {
            children.forEach(c => c.classList.toggle("hidden"));
        }, 2000);
    }
    toggleDetails(bannerEl, close = false) {
        this.debug("toggling details");
        const detailsContainerEl = bannerEl.querySelector(".global-banner__details");
        if (detailsContainerEl) {
            const closeDetails = close || detailsContainerEl.classList.contains("global-banner__details--open");
            if (closeDetails && detailsContainerEl.classList.contains("global-banner__details--open")) {
                detailsContainerEl.style.top = '0';
                if (this.focusTrap) {
                    this.focusTrap.deactivate();
                }
                detailsContainerEl.classList.remove("global-banner__details--open");
            }
            else if (!closeDetails && !detailsContainerEl.classList.contains("global-banner__details--open")) {
                detailsContainerEl.style.top = `${this.getBoundingClientRect().height}px`;
                this.focusTrap = createFocusTrap(detailsContainerEl);
                this.focusTrap.activate();
                detailsContainerEl.classList.add("global-banner__details--open");
            }
        }
    }
    display(toHide) {
        if (toHide) {
            const spaceTaken = this.container.offsetHeight;
            this.container.classList.add('global-banner--hidden');
            if ((isMobile() && this.device === 'mobile') || (!isMobile() && this.device === 'desktop')) {
                updateBodyPaddingTop(spaceTaken);
            }
        }
        else {
            this.container.classList.remove('global-banner--hidden');
        }
    }
    debug(msg) {
        console.debug(`global-banner ${this.device}: ${msg}`);
    }
    /**
     * to determine if it's the banner itself (as opposed to the banner details)
     */
    isBanner(el) {
        return !!(el.classList.contains("js-global-banner") || el.closest(".js-global-banner"));
    }
}
if (!window.customElements.get("global-banner")) {
    customElements.define("global-banner", GlobalBanner);
}
